import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Pagination,
  Stack,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Assignment as TicketIcon,
  CheckCircle as ResolvedIcon,
  HourglassEmpty as PendingIcon,
  Error as OpenIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  ConfirmationNumber as TicketNumberIcon,
  SupportAgent as SupportAgentIcon,
  AssignmentInd as AssignIcon,
  LowPriority as LowPriorityIcon,
  PriorityHigh as HighPriorityIcon,
} from "@mui/icons-material";
import { http as httpClient } from "../api/http";

// ==================== INTERFACES ====================

interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  awaitingResponse: number;
  resolved: number;
  closed: number;
  reopened: number;
  unassigned: number;
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  byCategory: Record<string, number>;
  avgFirstResponseTimeHours: number | null;
  avgResolutionTimeHours: number | null;
}

interface TicketMessage {
  id: number;
  message: string;
  isInternal: boolean;
  createdAt: string;
  sender: {
    id: number;
    fullName: string;
    userType: string;
  };
  attachments: TicketAttachment[];
}

interface TicketAttachment {
  id: number;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
}

interface Ticket {
  id: number;
  ticketNumber: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  requesterType: string;
  requesterName: string;
  requesterEmail: string;
  createdAt: string;
  updatedAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  requester: {
    id: number;
    fullName: string;
    email: string;
    mobileNumber: string;
    userType: string;
  };
  assignee?: {
    id: number;
    fullName: string;
    email: string;
  };
  messages?: TicketMessage[];
  attachments?: TicketAttachment[];
  _count?: {
    messages: number;
    attachments: number;
  };
}

interface Admin {
  id: number;
  fullName: string;
  email: string;
  userType: string;
}

interface PaginatedResponse {
  tickets: Ticket[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ==================== MAIN COMPONENT ====================

const TicketsManagement: React.FC = () => {
  // State
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0);

  // Fetch data
  const fetchStats = useCallback(async () => {
    try {
      const response = await httpClient.get("/admin/tickets/stats");
      setStats(response.data);
    } catch (err: any) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await httpClient.get("/admin/tickets/admins");
      setAdmins(response.data);
    } catch (err: any) {
      console.error("Error fetching admins:", err);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { page, limit: 15 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (searchQuery) params.search = searchQuery;

      // Handle tab-based filters
      if (tabValue === 1) params.unassigned = true;

      const response = await httpClient.get<PaginatedResponse>("/admin/tickets", { params });
      setTickets(response.data.tickets || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err: any) {
      console.error("Error fetching tickets:", err);
      setError(err.response?.data?.message || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, priorityFilter, categoryFilter, searchQuery, tabValue]);

  useEffect(() => {
    fetchStats();
    fetchAdmins();
  }, [fetchStats, fetchAdmins]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Handlers
  const handleViewTicket = async (ticket: Ticket) => {
    try {
      const response = await httpClient.get(`/admin/tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setDetailsOpen(true);
    } catch (err: any) {
      console.error("Error fetching ticket details:", err);
      alert("Failed to load ticket details");
    }
  };

  const handleUpdateTicket = async (ticketId: number, updates: any) => {
    try {
      await httpClient.put(`/admin/tickets/${ticketId}`, updates);
      fetchTickets();
      fetchStats();
      if (selectedTicket && selectedTicket.id === ticketId) {
        const response = await httpClient.get(`/admin/tickets/${ticketId}`);
        setSelectedTicket(response.data);
      }
    } catch (err: any) {
      console.error("Error updating ticket:", err);
      alert("Failed to update ticket");
    }
  };

  const handleSendMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) return;

    try {
      setSendingMessage(true);
      await httpClient.post(`/admin/tickets/${selectedTicket.id}/messages`, {
        message: newMessage,
        is_internal: isInternalNote,
      });

      // Refresh ticket details
      const response = await httpClient.get(`/admin/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data);
      setNewMessage("");
      setIsInternalNote(false);
    } catch (err: any) {
      console.error("Error sending message:", err);
      alert("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseTicket = async (ticketId: number) => {
    try {
      await httpClient.patch(`/admin/tickets/${ticketId}/close`);
      fetchTickets();
      fetchStats();
      if (selectedTicket && selectedTicket.id === ticketId) {
        const response = await httpClient.get(`/admin/tickets/${ticketId}`);
        setSelectedTicket(response.data);
      }
    } catch (err: any) {
      console.error("Error closing ticket:", err);
      alert("Failed to close ticket");
    }
  };

  const handleResolveTicket = async (ticketId: number) => {
    try {
      await httpClient.patch(`/admin/tickets/${ticketId}/resolve`);
      fetchTickets();
      fetchStats();
      if (selectedTicket && selectedTicket.id === ticketId) {
        const response = await httpClient.get(`/admin/tickets/${ticketId}`);
        setSelectedTicket(response.data);
      }
    } catch (err: any) {
      console.error("Error resolving ticket:", err);
      alert("Failed to resolve ticket");
    }
  };

  // Utility functions
  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      OPEN: "error",
      IN_PROGRESS: "info",
      AWAITING_RESPONSE: "warning",
      RESOLVED: "success",
      CLOSED: "default",
      REOPENED: "secondary",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      LOW: "default",
      MEDIUM: "info",
      HIGH: "warning",
      URGENT: "error",
    };
    return colors[priority] || "default";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  };

  const formatTimeAgo = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";

    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // ==================== STAT CARD COMPONENT ====================

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    subtext?: string;
  }> = ({ title, value, icon, color, subtext }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color }}>
              {value}
            </Typography>
            {subtext && (
              <Typography variant="caption" color="textSecondary">
                {subtext}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 1,
              color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ==================== RENDER ====================

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <TicketIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Support Tickets
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchTickets();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Tickets"
              value={stats.total}
              icon={<TicketIcon />}
              color="#3f51b5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Open"
              value={stats.open}
              icon={<OpenIcon />}
              color="#f44336"
              subtext={`${stats.unassigned} unassigned`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="In Progress"
              value={stats.inProgress}
              icon={<PendingIcon />}
              color="#ff9800"
              subtext={`${stats.awaitingResponse} awaiting response`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Resolved"
              value={stats.resolved}
              icon={<ResolvedIcon />}
              color="#4caf50"
              subtext={
                stats.avgResolutionTimeHours
                  ? `Avg: ${stats.avgResolutionTimeHours.toFixed(1)}h`
                  : undefined
              }
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs and Filters */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => {
            setTabValue(newValue);
            setPage(1);
          }}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="All Tickets" />
          <Tab
            label={
              <Badge badgeContent={stats?.unassigned || 0} color="error">
                Unassigned
              </Badge>
            }
          />
        </Tabs>

        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && fetchTickets()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="AWAITING_RESPONSE">Awaiting Response</MenuItem>
                  <MenuItem value="RESOLVED">Resolved</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                  <MenuItem value="REOPENED">Reopened</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="GENERAL">General</MenuItem>
                  <MenuItem value="TECHNICAL">Technical</MenuItem>
                  <MenuItem value="BILLING">Billing</MenuItem>
                  <MenuItem value="APPOINTMENT">Appointment</MenuItem>
                  <MenuItem value="ACCOUNT">Account</MenuItem>
                  <MenuItem value="FEEDBACK">Feedback</MenuItem>
                  <MenuItem value="COMPLAINT">Complaint</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={fetchTickets}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <TableContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : tickets.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="textSecondary">No tickets found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket #</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {ticket.ticketNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {ticket.subject}
                      </Typography>
                      {ticket._count && ticket._count.messages > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          {ticket._count.messages} messages
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                          {ticket.requesterName?.charAt(0) || "?"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {ticket.requesterName || "Unknown"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {ticket.requesterType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.status.replace("_", " ")}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority}
                        color={getPriorityColor(ticket.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ticket.category}</Typography>
                    </TableCell>
                    <TableCell>
                      {ticket.assignee ? (
                        <Typography variant="body2">
                          {ticket.assignee.fullName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="error">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(ticket.createdAt)}>
                        <Typography variant="body2">
                          {formatTimeAgo(ticket.createdAt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTicket(ticket);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Ticket Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">
                    <TicketNumberIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    {selectedTicket.ticketNumber}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedTicket.subject}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDetailsOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Ticket Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Requester
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar>
                        {selectedTicket.requester?.fullName?.charAt(0) || "?"}
                      </Avatar>
                      <Box>
                        <Typography>{selectedTicket.requester?.fullName}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedTicket.requester?.email}
                        </Typography>
                        <Typography variant="caption">
                          {selectedTicket.requester?.userType}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Details
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={selectedTicket.status.replace("_", " ")}
                        color={getStatusColor(selectedTicket.status)}
                        size="small"
                      />
                      <Chip
                        label={selectedTicket.priority}
                        color={getPriorityColor(selectedTicket.priority)}
                        size="small"
                      />
                      <Chip label={selectedTicket.category} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Created: {formatDate(selectedTicket.createdAt)}
                    </Typography>
                    {selectedTicket.firstResponseAt && (
                      <Typography variant="body2">
                        First Response: {formatDate(selectedTicket.firstResponseAt)}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Actions */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Assign To</InputLabel>
                      <Select
                        value={selectedTicket.assignee?.id || ""}
                        label="Assign To"
                        onChange={(e) =>
                          handleUpdateTicket(selectedTicket.id, {
                            assigned_to: e.target.value || null,
                          })
                        }
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {admins.map((admin) => (
                          <MenuItem key={admin.id} value={admin.id}>
                            {admin.fullName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={selectedTicket.priority}
                        label="Priority"
                        onChange={(e) =>
                          handleUpdateTicket(selectedTicket.id, {
                            priority: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="LOW">Low</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HIGH">High</MenuItem>
                        <MenuItem value="URGENT">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleResolveTicket(selectedTicket.id)}
                        disabled={
                          selectedTicket.status === "RESOLVED" ||
                          selectedTicket.status === "CLOSED"
                        }
                      >
                        Resolve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleCloseTicket(selectedTicket.id)}
                        disabled={selectedTicket.status === "CLOSED"}
                      >
                        Close
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Description */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {selectedTicket.description}
                </Typography>
              </Paper>

              {/* Messages */}
              <Typography variant="subtitle2" gutterBottom>
                Conversation
              </Typography>
              <Paper sx={{ p: 2, maxHeight: 400, overflow: "auto", mb: 2 }}>
                {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                  <List>
                    {selectedTicket.messages.map((msg, index) => (
                      <React.Fragment key={msg.id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            bgcolor: msg.isInternal ? "warning.light" : "transparent",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor:
                                  msg.sender.userType === "ADMIN" ||
                                  msg.sender.userType === "SUPER_ADMIN"
                                    ? "primary.main"
                                    : "secondary.main",
                              }}
                            >
                              {msg.sender.fullName?.charAt(0) || "?"}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {msg.sender.fullName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {msg.sender.userType}
                                </Typography>
                                {msg.isInternal && (
                                  <Chip
                                    label="Internal Note"
                                    size="small"
                                    color="warning"
                                  />
                                )}
                                <Typography variant="caption" color="textSecondary" sx={{ ml: "auto" }}>
                                  {formatTimeAgo(msg.createdAt)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                              >
                                {msg.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < selectedTicket.messages!.length - 1 && (
                          <Divider variant="inset" component="li" />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary" textAlign="center">
                    No messages yet
                  </Typography>
                )}
              </Paper>

              {/* New Message */}
              <Paper sx={{ p: 2 }}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sendingMessage}
                  />
                  <Box>
                    <Button
                      variant="contained"
                      color={isInternalNote ? "warning" : "primary"}
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      endIcon={<SendIcon />}
                      sx={{ mb: 1 }}
                    >
                      {sendingMessage ? "Sending..." : "Send"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color={isInternalNote ? "warning" : "inherit"}
                      onClick={() => setIsInternalNote(!isInternalNote)}
                      fullWidth
                    >
                      {isInternalNote ? "Internal Note" : "Public Reply"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TicketsManagement;
